import { GetStaticProps } from "next";
import { NextSeo } from "next-seo";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import { useRouter } from "next/router";
import { PageDataFetchError } from "@looksrare/utils";
import { HistoryView, YoloSupportedNetworks } from "@looksrare/yolo";
import { getCanonicalAndLanguageAlternates } from "@/utils/seo";
import { Page } from "@/components";
import { YoloGamesYoloProvider } from "@/components/YoloGamesYoloProvider";
import { getChainInfo } from "@/utils";

const path = "/yolo/history";

const HistoryPage: React.FC<React.PropsWithChildren<any>> = () => {
  const router = useRouter();
  const currentChainInfo = getChainInfo();

  const title = "YOLO | Past Rounds";
  const description = "See the winners of previous YOLO rounds, and verify the source of randomness used.";
  const { canonical, languageAlternates } = getCanonicalAndLanguageAlternates(path, router, currentChainInfo.appUrl);

  return (
    <Page title={title}>
      <NextSeo
        noindex
        nofollow
        title={title}
        description={description}
        openGraph={{
          url: canonical,
          title,
          description,
          images: [
            {
              url: `${currentChainInfo.appUrl}/images/yolo/meta.png`,
              alt: title,
              width: 1600,
              height: 840,
              type: "image/png",
            },
          ],
        }}
        canonical={canonical}
        languageAlternates={languageAlternates}
      />
      <YoloGamesYoloProvider>
        <HistoryView />
      </YoloGamesYoloProvider>
    </Page>
  );
};

export const getStaticProps: GetStaticProps = async ({ locale = "en" }) => {
  try {
    return {
      props: {
        ...(await serverSideTranslations(locale, ["yolo", "common"])),
      },
    };
  } catch (e) {
    console.error(e);
    throw new PageDataFetchError(path, e);
  }
};

export const getStaticPaths = async () => {
  return {
    paths: Object.values(YoloSupportedNetworks).map((network) => `/yolo/${network}/history`),
    fallback: "blocking",
  };
};

export default HistoryPage;
